import React, { CSSProperties, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import DialogBase from '../../../base/DialogBase/DialogBase';
import { ClassificationSortInput, useAssignContentsClassificationsMutation, useGetClassificationListWithTypeQuery, useGetClassificationTypeListQuery, GetContentDocument, GetContentQuery, GetContentQueryVariables, SearchContentsDocument, SearchContentsQuery, SearchContentsQueryVariables } from '../../../generated/graphql';
import { GetClassificationListWithTypeDocument, GetClassificationListWithTypeQuery, GetClassificationListWithTypeQueryVariables } from '../../../generated/graphql';
import { useApolloClient } from '@apollo/client';
import { ApolloQueryResult } from '@apollo/client';
import { FormAutoComplete } from '../../form';
import DataTableNew from '../../main/DataTableNew/DataTableNew';
import SearchBoxNew from '../../main/SearchBoxNew/SearchBoxNew';
import { X, Plus, AlertTriangle, RefreshCw, ListPlus, Tag } from 'lucide-react';
import styles from './AddContentsClassificationsDialog.module.css';
import { createGraphQLOrder, TableSort } from '../../main/DataTableNew/DataTableSorting';

type ClassificationEntry = {
    id: string;
    name: string;
    typeName: string;
    typeId: string;
    count?: number;
};

type AddContentsClassificationsDialogProps = {
    onClose: () => void,
    libraryId: string,
    ids: string[],
    visible: boolean,
    style?: CSSProperties | undefined,
    children?: ReactNode | undefined,
};

const AddContentsClassificationsDialog = (props: AddContentsClassificationsDialogProps) => {
    const { ids, style, visible, libraryId, onClose } = props;
    const [type, setType] = useState('');
    const [serverSearch, setServerSearch] = useState('');
    const [clientTerms, setClientTerms] = useState<string[]>([]);
    const [searchControlledValue, setSearchControlledValue] = useState<string | undefined>(undefined);
    const [validationError, setValidationError] = useState('');
    const [overwrite, setOverwrite] = useState(true);
    const [assignContentsClassifications, assignContentsClassificationsResult] = useAssignContentsClassificationsMutation();
    const apolloClient = useApolloClient();

    // Track original, added, and removed classifications
    const [originalClassifications, setOriginalClassifications] = useState<ClassificationEntry[]>([]);
    const [additions, setAdditions] = useState<ClassificationEntry[]>([]);
    const [removals, setRemovals] = useState<Set<string>>(new Set());
    const [contentLoading, setContentLoading] = useState(false);
    const [contentName, setContentName] = useState('');
    const [contentApplicationName, setContentApplicationName] = useState<string | null>(null);
    const [applyToSameName, setApplyToSameName] = useState(false);
    const [sort, setSort] = useState<TableSort | null>({ field: 'name', direction: 'asc' });

    // Fetch classifications from ALL selected content items and merge with counts
    useEffect(() => {
        if (ids.length === 0) return;
        setContentLoading(true);

        const fetchAll = async () => {
            const results = await Promise.all(
                ids.map(id =>
                    apolloClient.query<GetContentQuery, GetContentQueryVariables>({
                        query: GetContentDocument,
                        variables: { id },
                        fetchPolicy: 'no-cache',
                    }).catch(() => null)
                )
            );

            // Count how many content items have each classification
            const countMap = new Map<string, { entry: ClassificationEntry; count: number }>();
            for (const result of results) {
                const classifications = result?.data?.Content?.classifications;
                if (!classifications) continue;
                for (const c of classifications) {
                    if (!c) continue;
                    const key = c.id || '';
                    if (countMap.has(key)) {
                        countMap.get(key)!.count++;
                    } else {
                        countMap.set(key, {
                            entry: {
                                id: c.id || '',
                                name: c.name || '',
                                typeName: c.type?.name || '',
                                typeId: c.type?.id || '',
                            },
                            count: 1,
                        });
                    }
                }
            }

            const merged: ClassificationEntry[] = Array.from(countMap.values()).map(({ entry, count }) => ({
                ...entry,
                count,
            }));
            setOriginalClassifications(merged);
            if (ids.length === 1) {
                setContentName(results[0]?.data?.Content?.name || '');
                setContentApplicationName(results[0]?.data?.Content?.applicationName ?? null);
            } else {
                setContentName('');
                setContentApplicationName(null);
            }
            setContentLoading(false);
        };

        fetchAll();
    }, [ids, apolloClient]);

    const types = useGetClassificationTypeListQuery({
        fetchPolicy: "no-cache",
        variables: { contains: "" }
    });

    const classifications = useGetClassificationListWithTypeQuery({
        fetchPolicy: "no-cache",
        variables: { contains: serverSearch, typeId: type, order: createGraphQLOrder<ClassificationSortInput>(sort) },
        skip: !type
    });

    // Fetch all pages when client-side filtering is active (chips present)
    const [resetPagingKey, setResetPagingKey] = useState(0);
    const [allPagesNodes, setAllPagesNodes] = useState<any[]>([]);
    const fetchCancelRef = useRef<{ cancelled: boolean }>({ cancelled: false });

    useEffect(() => {
        if (clientTerms.length > 0 && type && serverSearch) {
            // Cancel any previous fetch
            fetchCancelRef.current.cancelled = true;
            const controller = { cancelled: false };
            fetchCancelRef.current = controller;

            setAllPagesNodes([]);
            setResetPagingKey(prev => prev + 1);

            const fetchAllPages = async () => {
                let after: string | undefined = undefined;
                let accumulated: any[] = [];
                while (!controller.cancelled) {
                    const result: ApolloQueryResult<GetClassificationListWithTypeQuery> = await apolloClient.query<GetClassificationListWithTypeQuery, GetClassificationListWithTypeQueryVariables>({
                        query: GetClassificationListWithTypeDocument,
                        variables: { contains: serverSearch, typeId: type, after, order: createGraphQLOrder<ClassificationSortInput>(sort) },
                        fetchPolicy: 'no-cache',
                    });
                    const nodes = result.data?.ClassificationList?.nodes || [];
                    accumulated = [...accumulated, ...nodes];
                    const pageInfo: GetClassificationListWithTypeQuery['ClassificationList'] extends { pageInfo?: infer T } ? T : any = result.data?.ClassificationList?.pageInfo;
                    if (!pageInfo?.hasNextPage) break;
                    after = pageInfo?.endCursor;
                }
                if (!controller.cancelled) setAllPagesNodes(accumulated);
            };

            fetchAllPages();
            return () => { controller.cancelled = true; };
        }
        // No client terms: cancel any running fetch and clear
        fetchCancelRef.current.cancelled = true;
        setAllPagesNodes([]);
        setResetPagingKey(prev => prev + 1);
    }, [clientTerms.length > 0, serverSearch, type, apolloClient, sort]); // eslint-disable-line react-hooks/exhaustive-deps

    // Data for the table: use allPagesNodes when client filtering, otherwise use paginated query
    const allNodes = useMemo(() => {
        if (clientTerms.length > 0 && allPagesNodes.length > 0) return allPagesNodes;
        if (!classifications.data) return [];
        return classifications.data.ClassificationList?.nodes || [];
    }, [clientTerms.length, allPagesNodes, classifications.data]);

    const filteredNodes = useMemo(() => {
        if (!allNodes) return allNodes;
        if (clientTerms.length === 0) return allNodes;
        return allNodes.filter(node => {
            if (!node) return false;
            const name = node.name || '';
            const desc = (node as any).description || '';
            const target = `${name} ${desc}`.toLowerCase();
            return clientTerms.every(term => target.includes(term.toLowerCase()));
        });
    }, [allNodes, clientTerms]);

    const handleSearchChanged = (value: string) => {
        setServerSearch(value);
        setResetPagingKey(prev => prev + 1);
    };

    const handleClientSearchTermsChanged = (terms: string[]) => {
        setClientTerms(terms);
    };

    const handleTypeChanged = (value: string) => {
        const selectedType = types.data?.ClassificationTypeList?.nodes?.filter(l => l?.name === value)[0] as any;
        if (selectedType) {
            setType(selectedType.id);
            setServerSearch('');
            setClientTerms([]);
            setSearchControlledValue('');
            setValidationError('');
        }
    };

    const handleAddClassification = (selectedIds: string[]) => {
        if (selectedIds.length === 0) return;
        const selectedId = selectedIds[selectedIds.length - 1];

        // Try to find the node in allNodes (full dataset)
        const lookupNodes = clientTerms.length > 0 && allPagesNodes.length > 0 ? allPagesNodes : classifications.data?.ClassificationList?.nodes || [];
        const classificationNode = lookupNodes.find((n: any) => n?.id === selectedId);
        if (!classificationNode) {
            return;
        }

        const selectedTypeName = types.data?.ClassificationTypeList?.nodes?.find(t => (t as any)?.id === type)?.name || '';

        const entry: ClassificationEntry = {
            id: classificationNode.id || '',
            name: classificationNode.name || '',
            typeName: selectedTypeName,
            typeId: type,
        };

        if (overwrite) {
            // Overwrite mode: one classification per type, full replacement logic
            const existingOriginal = originalClassifications.find(c => c.id === entry.id);
            if (existingOriginal) {
                if (removals.has(entry.id)) {
                    const next = new Set(removals);
                    next.delete(entry.id);
                    setRemovals(next);
                }
                return;
            }
            if (additions.find(c => c.id === entry.id)) return;

            const existingOfType = originalClassifications.find(c => c.typeId === entry.typeId);
            if (existingOfType && !removals.has(existingOfType.id)) {
                setRemovals(prev => new Set(prev).add(existingOfType.id));
            }
            setAdditions(prev => [...prev.filter(c => c.typeId !== entry.typeId), entry]);
        } else {
            // Append mode: enforce one classification per type
            // If the original already has this type, block it
            const existingOriginalOfType = originalClassifications.find(c => c.typeId === entry.typeId);
            if (existingOriginalOfType) {
                setValidationError(`A "${entry.typeName}" classification already exists. Switch to Overwrite mode to replace it.`);
                return;
            }
            // If an addition of this type already exists, replace it
            if (additions.find(c => c.id === entry.id)) return;
            setAdditions(prev => [...prev.filter(c => c.typeId !== entry.typeId), entry]);
        }
    };

    const handleRemoveClassification = (id: string) => {
        // If it's an addition, just remove it from additions
        if (additions.find(c => c.id === id)) {
            setAdditions(prev => prev.filter(c => c.id !== id));
            return;
        }
        // If it's an original, mark as removed
        setRemovals(prev => new Set(prev).add(id));
    };

    const handleUndoRemoval = (id: string) => {
        // Undo a removal — but enforce one-per-table
        const entry = originalClassifications.find(c => c.id === id);
        if (!entry) return;

        // Remove any addition of the same type since the original is being restored
        setAdditions(prev => prev.filter(c => c.typeId !== entry.typeId));

        const next = new Set(removals);
        next.delete(id);
        setRemovals(next);
    };

    // Compute the working list for display
    const workingList: (ClassificationEntry & { status: 'existing' | 'added' | 'removed' })[] = [
        ...originalClassifications.map(c => ({
            ...c,
            status: removals.has(c.id) ? 'removed' as const : 'existing' as const,
        })),
        ...additions.map(c => ({
            ...c,
            status: 'added' as const,
        })),
    ];

    const hasChanges = additions.length > 0 || removals.size > 0;

    const handleSubmit = async () => {
        if (!hasChanges) {
            setValidationError('No changes to apply.');
            return;
        }
        setValidationError('');

        let targetIds = ids;

        if (applyToSameName && ids.length === 1 && contentName) {
            try {
                const result = await apolloClient.query<SearchContentsQuery, SearchContentsQueryVariables>({
                    query: SearchContentsDocument,
                    variables: {
                        where: {
                            name: { eq: contentName },
                            libraryId: { eq: libraryId },
                        },
                    },
                    fetchPolicy: 'no-cache',
                });
                const sameNameIds = (result.data?.ContentList?.nodes || [])
                    .filter(n => n?.applicationName !== contentApplicationName)
                    .map(n => n?.id)
                    .filter((id): id is string => !!id && !ids.includes(id));
                targetIds = [...ids, ...sameNameIds];
            } catch {
                // fall back to just the original ids
            }
        }

        if (overwrite) {
            // Overwrite: send full final list
            const finalIds = [
                ...originalClassifications.filter(c => !removals.has(c.id)).map(c => c.id),
                ...additions.map(c => c.id),
            ];
            assignContentsClassifications({
                variables: { ids: targetIds, libraryId, classificationIds: finalIds, overwrite: true }
            })
                .then(() => { onClose(); })
                .catch(() => {});
        } else {
            // Append: send only the new additions, overwrite=false
            const additionIds = additions.map(c => c.id);
            assignContentsClassifications({
                variables: { ids: targetIds, libraryId, classificationIds: additionIds, overwrite: false }
            })
                .then(() => { onClose(); })
                .catch(() => {});
        }
    };

    return (
        <DialogBase
            title="Manage Classifications"
            type="OkCancel"
            visible={visible}
            onClose={onClose}
            onSubmit={handleSubmit}
            validationError={validationError}
            className={styles.AddContentsClassificationsDialog}
            style={style}
            width="1000px"
            isLoading={assignContentsClassificationsResult.loading || types.loading || contentLoading}>
            {ids.length > 1 && overwrite && (
                <div className={styles.warningBanner}>
                    <AlertTriangle size={15} />
                    <span>{ids.length} content items selected — changes will overwrite classifications on all selected items.</span>
                </div>
            )}
            <div className={styles.splitLayout}>

            {/* Classification type picker + table */}
            <div className={styles.addSection}>
                <label className={styles.changesLabel}>Add Classification</label>
                <FormAutoComplete
                    label="Classification Table"
                    name="type"
                    value=""
                    onChange={handleTypeChanged}
                    options={(types.data?.ClassificationTypeList?.nodes?.map(p => p?.name) || []).filter((n): n is string => !!n)}
                />
                {type && (
                    <div className={styles.tableSection}>
                        <div className={styles.tableSearch}>
                            <SearchBoxNew
                                onSearchChanged={handleSearchChanged}
                                onClientSearchTermsChanged={handleClientSearchTermsChanged}
                                controlledValue={searchControlledValue}
                            />
                            {ids.length === 1 && contentName && (
                                <button
                                    type="button"
                                    className={styles.useNameButton}
                                    onClick={() => setSearchControlledValue(contentName)}
                                    title={`Fill search with content name: "${contentName}"`}
                                >
                                    <Tag size={12} />
                                    <span className={styles.useNameText}>{contentName}</span>
                                </button>
                            )}
                        </div>
                        <div className={styles.tableContainer}>
                            <DataTableNew
                                sortingMode="server"
                                sort={sort}
                                onSortChange={setSort}
                                isLoading={classifications.loading}
                                data={filteredNodes}
                                key={resetPagingKey}
                                showDescription
                                searchable={false}
                                selectable={false}
                                onRowClick={(row) => setSearchControlledValue(row.name || '')}
                                columns={[
                                    {
                                        id: 'add',
                                        name: '',
                                        width: '50px',
                                        render: (row: any) => (
                                            <button
                                                onClick={(e) => { e.stopPropagation(); handleAddClassification([row.id]); }}
                                                className="btn-primary"
                                                style={{ padding: '2px 8px', fontSize: '0.72rem', lineHeight: 1.4 }}
                                                title="Add classification"
                                            >
                                                <Plus size={13} />
                                            </button>
                                        ),
                                    },
                                ]}
                                fetchMore={() => classifications.data?.ClassificationList?.pageInfo?.hasNextPage && classifications.fetchMore({
                                    variables: {
                                        contains: serverSearch,
                                        after: classifications.data?.ClassificationList?.pageInfo.endCursor,
                                        order: createGraphQLOrder<ClassificationSortInput>(sort),
                                    },
                                    updateQuery: (previousResult, { fetchMoreResult }) => {
                                        if (!fetchMoreResult) return previousResult;
                                        const previousNodes = previousResult.ClassificationList?.nodes || [];
                                        const newNodes = fetchMoreResult.ClassificationList?.nodes || [];
                                        return {
                                            ...previousResult,
                                            ClassificationList: {
                                                nodes: [...previousNodes, ...newNodes],
                                                pageInfo: fetchMoreResult.ClassificationList?.pageInfo as any
                                            }
                                        };
                                    }
                                })}
                            />
                        </div>
                    </div>
                )}
            </div>

            {/* Right panel: Current & pending classifications list */}
            <div className={styles.changesPanel}>
                <div className={styles.changesPanelHeader}>
                    <label className={styles.changesLabel}>Classifications</label>
                    <button
                        type="button"
                        className={`${styles.modeToggle} ${overwrite ? styles.modeOverwrite : styles.modeAppend}`}
                        onClick={() => { setOverwrite(prev => !prev); setRemovals(new Set()); setAdditions([]); }}
                        title={overwrite ? 'Overwrite mode: replaces all classifications' : 'Append mode: adds to existing classifications'}
                    >
                        {overwrite ? <RefreshCw size={12} /> : <ListPlus size={12} />}
                        {overwrite ? 'Overwrite' : 'Append'}
                    </button>
                </div>
                <div className={styles.changesList}>
                    {workingList.length === 0 && (
                        <div className={styles.emptyState}>No classifications assigned</div>
                    )}
                    {workingList.map(c => (
                        <div
                            key={c.id}
                            className={`${styles.changesItem} ${c.status === 'removed' ? styles.removed : ''} ${c.status === 'added' ? styles.added : ''} ${!overwrite && c.status === 'existing' ? styles.shaded : ''}`}
                        >
                            <div className={styles.changesItemContent}>
                                <span className={styles.changesTypeBadge}>
                                    {c.typeName}
                                    {ids.length > 1 && c.status !== 'added' && c.count !== undefined && (
                                        <span className={styles.countBadge}>{c.count}/{ids.length}</span>
                                    )}
                                </span>
                                <span className={styles.changesName}>{c.name}</span>
                            </div>
                            {overwrite && c.status === 'removed' ? (
                                <button
                                    className={styles.undoButton}
                                    onClick={() => handleUndoRemoval(c.id)}
                                    title="Undo removal"
                                >
                                    Undo
                                </button>
                            ) : (c.status === 'added' || (overwrite && c.status === 'existing')) ? (
                                <button
                                    className={styles.removeButton}
                                    onClick={() => handleRemoveClassification(c.id)}
                                    title="Remove classification"
                                >
                                    <X size={14} />
                                </button>
                            ) : null}
                        </div>
                    ))}
                </div>
            </div>

            </div>
            {ids.length === 1 && (
                <div className={styles.applyToSameNameRow}>
                    <label className={styles.applyToSameNameLabel}>
                        <input
                            type="checkbox"
                            checked={applyToSameName}
                            onChange={(e) => setApplyToSameName(e.target.checked)}
                            className={styles.applyToSameNameCheckbox}
                        />
                        Apply to all content with the same name but a different Application Name in this library
                    </label>
                </div>
            )}
        </DialogBase>
    );
};

export default AddContentsClassificationsDialog;
